// Client-side state for the post-login "choose a plan" flow:
// Pricing (select) -> /login -> /choose-plan modal -> /checkout.
// Everything is localStorage (survives OAuth redirects and the email-link tab); reads are
// wrapped because storage can be unavailable (private mode / blocked).

const PENDING_KEY = 'chooseplan.pending';
const PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DISMISSED_PREFIX = 'chooseplan.dismissedAt:';
const PAYG_CHOSEN_PREFIX = 'chooseplan.paygChosen:';
const SHOWN_SESSION_PREFIX = 'chooseplan.shown:';
const DAY_MS = 24 * 60 * 60 * 1000;

export type PendingSelection = { kind: 'plan'; planId: string } | { kind: 'payg' };

interface StoredPending {
  selection: PendingSelection;
  savedAt: number;
}

export interface ChoosePlanPromptSettings {
  enabled: boolean;
  repromptDays: number;
}

export const formatMoney = (currency: 'INR' | 'USD' | undefined, value: number) =>
  currency === 'INR' ? `₹${value.toFixed(2)}` : `$${value.toFixed(2)}`;

const readLocal = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeLocal = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
};
const removeLocal = (key: string) => {
  try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
};

// A fresh selection from Pricing must be able to open the prompt even if it was already shown this session.
const clearShownFlags = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(SHOWN_SESSION_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch { /* storage unavailable */ }
};

export function setPendingSelection(selection: PendingSelection): void {
  const stored: StoredPending = { selection, savedAt: Date.now() };
  writeLocal(PENDING_KEY, JSON.stringify(stored));
  clearShownFlags();
}

export function getPendingSelection(): PendingSelection | null {
  const raw = readLocal(PENDING_KEY);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as Partial<StoredPending>;
    const selection = stored.selection;
    if (!selection || typeof stored.savedAt !== 'number' || Date.now() - stored.savedAt > PENDING_TTL_MS) {
      removeLocal(PENDING_KEY);
      return null;
    }
    if (selection.kind === 'payg') return selection;
    if (selection.kind === 'plan' && typeof selection.planId === 'string' && selection.planId) return selection;
  } catch { /* fall through to clear */ }
  removeLocal(PENDING_KEY);
  return null;
}

export function clearPendingSelection(): void {
  removeLocal(PENDING_KEY);
}

export function markDismissed(userId: string): void {
  writeLocal(`${DISMISSED_PREFIX}${userId}`, String(Date.now()));
}

// PAYG has no payment step, so "user already picked PAYG" is remembered here (per browser) to stop re-prompting.
export function markPaygChosen(userId: string): void {
  writeLocal(`${PAYG_CHOSEN_PREFIX}${userId}`, '1');
}

export function clearPaygChosen(userId: string): void {
  removeLocal(`${PAYG_CHOSEN_PREFIX}${userId}`);
}

// The prompt is shown at most once per browser session, so closing it (or visiting the dashboard
// again) does not bring it straight back.
const wasShownThisSession = (userId: string): boolean => {
  try { return sessionStorage.getItem(`${SHOWN_SESSION_PREFIX}${userId}`) === '1'; } catch { return false; }
};
export function markShownThisSession(userId: string): void {
  try { sessionStorage.setItem(`${SHOWN_SESSION_PREFIX}${userId}`, '1'); } catch { /* storage unavailable */ }
}

export interface PromptDecisionInput {
  userId: string;
  settings: ChoosePlanPromptSettings;
  // From /api/me/pricing: user is on a subscription plan.
  hasAssignedPlan: boolean;
}

/**
 * A pending selection from Pricing opens the prompt even for users with a plan (so upgrades reach
 * checkout) and even if the admin switch is off. Otherwise it opens for unpaid users only: admin
 * switch on, no plan, not already on PAYG by choice, and not dismissed within the admin
 * "re-prompt after N days" window. Either way, at most once per browser session.
 */
export function shouldOpenChoosePlan({ userId, settings, hasAssignedPlan }: PromptDecisionInput): boolean {
  if (wasShownThisSession(userId)) return false;
  if (getPendingSelection()) return true;
  if (!settings.enabled) return false;
  if (hasAssignedPlan) return false;
  if (readLocal(`${PAYG_CHOSEN_PREFIX}${userId}`)) return false;
  const dismissedAt = Number(readLocal(`${DISMISSED_PREFIX}${userId}`));
  if (Number.isFinite(dismissedAt) && dismissedAt > 0 && settings.repromptDays > 0) {
    return Date.now() - dismissedAt >= settings.repromptDays * DAY_MS;
  }
  return true;
}
